import { Fragment, useId, type ReactElement } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { resolveLandingPathWithSet } from '@/lib/use-landing-page';
import { EmergeMark } from '@/components/EmergeMark';
import { Badge, Button } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { CountBadge } from '@/components/ui/CountBadge';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
import { useAuth } from '@/lib/use-auth';
import { cx } from '@/lib/cx';
import {
  NAV_GROUPS,
  NAV_STANDALONE,
  isNavItemOffered,
  type NavExternalSource,
  type NavItem,
} from '@/lib/nav';
import { useNavGroups } from '@/lib/use-sidebar';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';
import { useEnquirySummary } from '@/lib/use-enquiry-summary';

/*
 * 32px rows on a desktop, because a sidebar is chrome and should not eat the
 * screen. The drawer only ever renders below `lg`, so `max-lg:h-11` lifts the
 * same row to a 44px touch target there without a second code path.
 */
const rowBase =
  'relative flex items-center rounded-md text-sm transition-colors duration-150 ease-out motion-reduce:transition-none';
const rowExpanded = 'h-8 w-full gap-2.5 pl-3 pr-2 max-lg:h-11';
const rowCollapsed = 'h-8 w-full justify-center';

const rowInactive = 'text-fg-muted hover:bg-surface-hover hover:text-fg';
const rowActive = 'bg-accent-brand-soft font-medium text-accent-brand';

interface SidebarProps {
  /** Icon-rail mode. The drawer always passes `false` — it has room for labels. */
  collapsed: boolean;
  /** Desktop only: renders the rail toggle at the foot of the column. */
  onToggleCollapse?: () => void;
  /** Drawer only: following a link dismisses it. */
  onNavigate?: () => void;
  /** Drawer only: renders the close button in the brand row. */
  onClose?: () => void;
}

/* Lucide's `log-out`, inlined at 24×24 — the app carries no icon dependency. */
function SignOutIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function ActiveMarker() {
  return (
    <span
      aria-hidden="true"
      className="absolute inset-y-1.5 left-0 w-0.5 rounded-r-sm bg-accent-brand"
    />
  );
}

function NavRow({
  item,
  collapsed,
  onNavigate,
  href,
  count,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
  /**
   * How many things on this row's page are waiting, for a row carrying a
   * `countKey`. `null`/`undefined`/`0` draw nothing — see `CountBadge`.
   */
  count?: number | null;
  /**
   * The resolved destination of an EXTERNAL row. Never set on a routed one —
   * `NavItem`'s two arms make that a compile error rather than a convention.
   */
  href?: string;
}) {
  const shape = cx(rowBase, collapsed ? rowCollapsed : rowExpanded);
  const fullLabel = item.fullLabel ?? item.label;
  const external = item.externalSource !== undefined;
  /*
   * The badge is `aria-hidden`, so the number goes into the NAME instead — and
   * into the collapsed rail's tooltip, where the badge is too small to read on
   * its own. Only a positive count adds anything; `CountBadge` draws nothing
   * for the rest, and the name must not claim what the row does not show.
   */
  const hasCount = typeof count === 'number' && Number.isFinite(count) && count > 0;
  const routedName = hasCount ? `${fullLabel}, ${count} to deal with` : fullLabel;
  /*
   * "(opens in a new tab)" is not helper text: it is the one thing the icon
   * beside the label tells a sighted reader and nothing otherwise tells anyone
   * else. In the collapsed rail there is no icon and no label either, so this
   * string is the row's ENTIRE accessible name.
   */
  const accessibleName = external
    ? `${fullLabel} (opens in a new tab)`
    : item.to
      ? routedName
      : `${fullLabel} — coming soon`;

  /*
   * The visible label can be an abbreviation, so it is hidden from assistive
   * tech and the full name supplied beside it. Collapsed, the `sr-only` span is
   * the only name the row has — the visible one is not rendered at all.
   *
   * ⚠️ *SUPERSEDED 2026-09-02: the example here was `("Revenue" for "Revenue
   * Tracker")`.* **That row is now called `Revenue Tracker` outright** — the
   * owner renamed all seven Dashboards and Trackers rows to their full names, so
   * no nav row abbreviates today and `fullLabel` is unset on every one of them.
   * The mechanism is unchanged and still correct for the next abbreviation; only
   * the example died, which is the failure this codebase records as *an artefact
   * falsified by a change somewhere else*. An example is evidence for exactly the
   * case it exercised.
   */
  const label = (
    <>
      <span className="sr-only">{accessibleName}</span>
      {!collapsed && (
        <span aria-hidden="true" className="min-w-0 flex-1 truncate">
          {item.label}
        </span>
      )}
    </>
  );

  /*
   * Collapsed, the rail row shows an icon and nothing else, so it carries the
   * styled hover label instead of a native `title` (never both — that would be
   * two labels for one row). Expanded, the visible text is the label and the
   * row is left exactly as it was. `describes={false}`: the `sr-only` span is
   * already the row's accessible name, so describing it again would announce
   * it twice. `flex w-full` keeps the wrapper from shrinking the full-width row.
   */
  const withTip = (row: ReactElement, tipLabel: string) =>
    collapsed ? (
      <Tooltip label={tipLabel} describes={false} className="flex w-full">
        {row}
      </Tooltip>
    ) : (
      row
    );

  /*
   * A real anchor, and NOT a `NavLink`: this leaves the app, so client-side
   * routing would turn an absolute URL into a route that does not exist and
   * land the reader on the app's own 404 with the address mangled.
   *
   * ⚠️ It renders NOTHING without a destination rather than an inert row. The
   * "Soon" treatment below is for an area of the product that is not built yet
   * — a promise about the roadmap. An external row with no URL is not that: it
   * means the integration is switched off on this deployment, and telling a
   * reader that Google Sheets is "coming soon" would be a claim about a
   * decision nobody has taken. `Sidebar` filters these out before they get
   * here; this is the guard that makes the `href` below non-null rather than
   * asserted.
   */
  if (external) {
    if (!href) return null;

    return (
      <li>
        {withTip(
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            // Dismisses the mobile drawer. The new tab takes focus, and leaving
            // the drawer open over the app to come back to is worse than not.
            onClick={onNavigate}
            title={collapsed ? undefined : accessibleName}
            className={cx(shape, rowInactive)}
          >
            {item.icon}
            {label}
            {!collapsed && <ExternalTabIcon className="h-3 w-3 text-fg-subtle" />}
          </a>,
          accessibleName,
        )}
      </li>
    );
  }

  // No `to` yet: render inert markup rather than a link to a dead route, so it
  // is neither clickable nor reachable by keyboard.
  if (!item.to) {
    return (
      <li>
        {withTip(
          <span
            aria-disabled="true"
            title={collapsed ? undefined : accessibleName}
            className={cx(shape, 'cursor-not-allowed text-fg-subtle')}
          >
            {item.icon}
            {label}
            {!collapsed && (
              <span aria-hidden="true" className="shrink-0">
                <Badge tone="neutral">Soon</Badge>
              </span>
            )}
          </span>,
          accessibleName,
        )}
      </li>
    );
  }

  return (
    <li>
      {withTip(
        <NavLink
          to={item.to}
          onClick={onNavigate}
          title={collapsed ? undefined : routedName}
          className={({ isActive }) => cx(shape, isActive ? rowActive : rowInactive)}
        >
          {({ isActive }) => (
            <>
              {isActive && <ActiveMarker />}
              {item.icon}
              {label}
              {/*
               * Expanded: after the label, whose span is `flex-1`, so the badge
               * sits at the row's right edge. Collapsed: pinned to the icon's
               * top-right corner — the row is `relative` and the icon centred,
               * so this reads as a notification dot on the icon itself.
               */}
              {collapsed ? (
                <CountBadge count={count} size="sm" className="absolute top-0.5 right-1.5" />
              ) : (
                <CountBadge count={count} className="ml-auto shrink-0" />
              )}
            </>
          )}
        </NavLink>,
        routedName,
      )}
    </li>
  );
}

/**
 * The sidebar column itself. It fills whatever box it is given — AppShell owns
 * the fixed desktop rail and the mobile drawer that wrap it.
 */
/*
 * Passed to useNavGroups so a remembered section that has since been renamed or
 * removed resolves to "nothing open" instead of to a label the nav no longer
 * has. Module-level: a new array identity each render would be a new "all
 * labels" list every time.
 */
const NAV_GROUP_LABELS = NAV_GROUPS.map((group) => group.label);

export function Sidebar({ collapsed, onToggleCollapse, onNavigate, onClose }: SidebarProps) {
  const [openGroups, onToggleGroup] = useNavGroups(NAV_GROUP_LABELS);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, ability } = useAuth();
  const navId = useId();

  /*
   * Where each external nav row actually points, resolved at render because the
   * URL is not knowable at import — see `NavExternalSource` in `@/lib/nav`.
   *
   * ⚠️ ONE SHARED READ, NOT ONE PER PAGE. This sidebar is mounted on every
   * signed-in route and `/client-deliverables` asks the same question again for
   * its own button, so a naive fetch here would be a request on every
   * navigation plus a second one on the tracker. `useTrackerSheetLink` is a
   * module-level cache: N callers, one request per tab, and no provider to
   * mount.
   *
   * ⚠️ `loading` COLLAPSES INTO `null` on purpose. A row that appears a moment
   * after first paint pushes the four rows under it down as somebody is
   * reaching for one — so an unknown destination is treated exactly as no
   * destination, and the row arrives once, or not at all.
   */
  const trackerSheet = useTrackerSheetLink();
  const externalHrefs: Record<NavExternalSource, string | null> = {
    'client-deliverables-sheet': trackerSheet.loading ? null : trackerSheet.link,
  };
  const hrefFor = (item: NavItem): string | undefined =>
    item.externalSource ? (externalHrefs[item.externalSource] ?? undefined) : undefined;

  /*
   * The number a row's `countKey` names, resolved here for the same reason the
   * external hrefs are: `@/lib/nav` is static config and a count is live data.
   * `null` means not known — never zero — and draws no badge.
   */
  const enquiries = useEnquirySummary();
  const counts: Record<NonNullable<NavItem['countKey']>, number | null> = {
    enquiries: enquiries.total,
  };
  const countFor = (item: NavItem): number | null | undefined =>
    item.countKey ? counts[item.countKey] : undefined;

  function onSignOut() {
    logout();
    // Replace rather than push: Back must not land on a page the session no
    // longer has a token for.
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface">
      <div
        className={cx(
          'flex h-14 shrink-0 items-center border-b border-border',
          collapsed ? 'justify-center px-2' : 'gap-2.5 px-3',
        )}
      >
        {/*
         * The mark and the name open this person's permission-set default
         * dashboard. Resolved with `stored: null` on purpose: the owner asked
         * for the SET's default here, not the reader's own saved landing page,
         * which is what `/` (LandingRedirect) honours.
         */}
        <Link
          to={resolveLandingPathWithSet(null, ability.landingPath, ability)}
          onClick={onNavigate}
          aria-label="SEED EMERGE — go to your default dashboard"
          className={cx(
            'flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
            collapsed ? '' : 'min-w-0 flex-1 gap-2.5',
          )}
        >
          <span className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-md border border-border">
            <EmergeMark className="h-full w-full p-0.5" />
          </span>

          {!collapsed && (
            <span className="min-w-0 flex-1 leading-none">
              <span className="block truncate text-sm font-semibold tracking-[-0.01em] text-fg">
                SEED EMERGE
              </span>
              <span className="mt-1 block truncate text-2xs font-medium text-fg-subtle">
                Partnerships &amp; Billing
              </span>
            </span>
          )}
        </Link>

        {onClose && (
          <Button
            variant="ghost"
            iconOnly
            onClick={onClose}
            aria-label="Close navigation"
            leadingIcon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="h-5 w-5"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            }
          />
        )}
      </div>

      <nav aria-label="Primary" className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {NAV_GROUPS.map((group, index) => {
          const sectionId = `${navId}-${index}`;
          /*
           * Rows the API refuses outright are dropped rather than disabled.
           * The audit log 403s anyone below the admin tier even to read it, so
           * there is no partial page to offer — only a dead end. This is NOT
           * the rule for pages with admin-only controls; those stay visible to
           * everyone and gate the controls instead.
           */
          const items = group.items.filter(
            (item) =>
              /*
               * The three visibility tests — `adminOnly`, `superAdminOnly`, and
               * the Dashboards/Trackers surface modules — live in
               * `isNavItemOffered` because `GlobalSearch.tsx` asks the identical
               * question about the identical rows. Two copies would drift, and
               * what would drift is who can see which page. Read its header
               * before changing any of the three: it DEGRADES OPEN on the module
               * test, and a routeless row that survives still renders its inert
               * "Soon" state — gating decides whether the row exists, not how it
               * draws.
               */
              isNavItemOffered(item, ability) &&
              /*
               * An external row with nowhere to go is dropped, not disabled.
               * The integration is switched off on this deployment (or the
               * answer has not arrived), and a control whose only outcome is a
               * dead tab is worse than no control — the same rule the Xero
               * `sync` affordance follows. Routed rows are unaffected: `hrefFor`
               * returns undefined for them and this test passes.
               */
              (!item.externalSource || hrefFor(item) !== undefined),
          );
          if (items.length === 0) return null;
          // In the icon rail there are no labels to click, so a collapsed
          // section would simply be unreachable — groups always show there.
          const expanded = collapsed || openGroups.has(group.label);
          const containsCurrent = items.some(
            (item) => item.to && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
          );

          return (
            <div
              key={group.label}
              /*
               * A rule and real space above each section after the first, so
               * several open sections still read as separate blocks rather than
               * one long list. Ternary rather than `cx` of two spacing classes:
               * conflicting Tailwind utilities resolve by stylesheet order, not
               * by the order they are written here.
               */
              className={
                index === 0 ? undefined : collapsed ? 'mt-3' : 'mt-4 border-t border-border pt-3'
              }
            >
              {collapsed ? (
                // A rail has no room for a section label; a hairline does the same job.
                index > 0 && <div aria-hidden="true" className="mx-auto mb-3 h-px w-6 bg-border" />
              ) : (
                /*
                 * Deliberately not styled like a nav row. A header and a
                 * destination reading at the same weight is what stopped the
                 * structure registering, so the header goes smaller, bolder and
                 * *darker* than the rows it introduces (`fg-secondary` against
                 * their `fg-muted`), plus the chevron — none of them colour
                 * alone. Sentence case, no tracking, no uppercase: one quiet
                 * label style (2026-10 UI rework). ⛔ The LABEL TEXT is the
                 * `emerge.sidebar.openGroups` storage key — never change it.
                 * `max-lg:min-h-11` matches the rows' touch bump in the drawer.
                 */
                <button
                  type="button"
                  onClick={() => onToggleGroup(group.label)}
                  aria-expanded={expanded}
                  aria-controls={sectionId}
                  className="mb-1 flex min-h-8 w-full items-center gap-1.5 rounded-md px-3 text-xs font-semibold text-fg-secondary transition-colors duration-150 ease-out hover:bg-surface-hover hover:text-fg motion-reduce:transition-none max-lg:min-h-11"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className={cx(
                      'h-3 w-3 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
                      expanded && 'rotate-90',
                    )}
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                  <span className="min-w-0 flex-1 truncate text-left">{group.label}</span>
                  {/*
                   * With every section closed by default the active page can be
                   * out of sight, so a closed section that holds it says so —
                   * otherwise the nav stops answering "where am I?".
                   */}
                  {containsCurrent && !expanded && (
                    <>
                      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-accent-brand" />
                      <span className="sr-only">contains the current page</span>
                    </>
                  )}
                </button>
              )}

              <ul
                id={sectionId}
                aria-label={group.label}
                hidden={!expanded}
                className="m-0 flex list-none flex-col gap-0.5 p-0"
              >
                {items.map((item, itemIndex) => {
                  // Stage sub-heading (Product Insights: Attract · Nurture ·
                  // Convert) above the first visible row of each run. The rail
                  // has no room for text, so it gets a short hairline instead.
                  const stage = item.stage;
                  const startsStage =
                    stage !== undefined && (itemIndex === 0 || items[itemIndex - 1]?.stage !== stage);
                  return (
                    <Fragment key={item.fullLabel ?? item.label}>
                      {startsStage &&
                        (collapsed ? (
                          itemIndex > 0 && (
                            <li aria-hidden="true" className="mx-auto my-1.5 h-px w-4 bg-border" />
                          )
                        ) : (
                          <li
                            className={cx(
                              'px-3 pb-0.5 text-xs font-medium text-fg-subtle',
                              itemIndex === 0 ? 'pt-0.5' : 'pt-2',
                            )}
                          >
                            {stage}
                          </li>
                        ))}
                      <NavRow
                        item={item}
                        collapsed={collapsed}
                        onNavigate={onNavigate}
                        href={hrefFor(item)}
                        count={countFor(item)}
                      />
                    </Fragment>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {/*
         * Top-level rows, not a disclosure: these have no section of their own
         * to belong to, so they sit below the groups with no header. Same
         * NavRow as everything else, so active state, the rail, tooltips and
         * the "Soon" treatment need no second implementation.
         */}
        {NAV_STANDALONE.length > 0 && (
          <div className={collapsed ? 'mt-3' : 'mt-4 border-t border-border pt-3'}>
            {collapsed && <div aria-hidden="true" className="mx-auto mb-3 h-px w-6 bg-border" />}
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {/*
               * `hrefFor` is passed here too although nothing standalone is
               * external today. Leaving it off would work, and would fail
               * silently the day somebody adds an external row here — it would
               * return `null` from `NavRow`'s guard and simply not appear, with
               * nothing to debug.
               */}
              {NAV_STANDALONE.map((item) => (
                <NavRow
                  key={item.fullLabel ?? item.label}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  href={hrefFor(item)}
                  count={countFor(item)}
                />
              ))}
            </ul>
          </div>
        )}
      </nav>

      {/*
       * Collapse sits ABOVE Sign out, and Sign out sits below a divider
       * (owner's instruction, 2026-09-26): it ends the session, so it must not
       * read as one more row a hand can slide onto while reaching for
       * Collapse. ⚠️ It is NOT red any more (owner decision, 2026-10 UI
       * rework): the divider separates it; a danger fill made it the loudest
       * element on every page.
       *
       * Sign out lives here as well as in the account menu: the sidebar is where
       * the user is told the app's structure lives, and two doors to one action
       * costs nothing. `max-lg:h-11` matches the nav rows' touch target, and
       * beats the primitive's own `max-sm` bump to 40px on a phone.
       */}
      <div className="flex shrink-0 flex-col gap-0.5 border-t border-border p-2">
        {onToggleCollapse && (
          <Button
            variant="ghost"
            size="sm"
            iconOnly={collapsed}
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={collapsed ? 'mx-auto' : 'w-full justify-start'}
            leadingIcon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="h-4 w-4 shrink-0"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M9 3v18" />
                <path d={collapsed ? 'm13 9 3 3-3 3' : 'm16 9-3 3 3 3'} />
              </svg>
            }
          >
            Collapse
          </Button>
        )}

        <div aria-hidden="true" className={cx('my-1.5 h-px bg-border', collapsed ? 'mx-auto w-6' : 'w-full')} />

        <Button
          variant="ghost"
          size="sm"
          iconOnly={collapsed}
          onClick={onSignOut}
          aria-label={collapsed ? 'Sign out' : undefined}
          title="Sign out"
          className={cx('max-lg:h-11 max-sm:h-11', collapsed ? 'mx-auto' : 'w-full justify-start')}
          leadingIcon={<SignOutIcon />}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
}
